import { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { fetch_fuel_logs, update_fuel_log, get_last_fuel_log, add_fuel_log } from '../../api/fuel_logs';
import { fetch_all_vehicles } from '../../api/vehicles';
import { fetch_all_profiles } from '../../api/profiles';
import { ActivityIndicator } from './ActivityIndicator';
import LocationMapModal from '../LocationMapModal';

const FuelLogs = ({ theme, isManagement = false }) => {
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d;
    });
    const [endDate, setEndDate] = useState(new Date());
    const [fuelLogs, setFuelLogs] = useState([]);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [vehicles, setVehicles] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [allDrivers, setAllDrivers] = useState([]);

    // Filters
    const [vehicleFilter, setVehicleFilter] = useState('all');
    const [driverFilter, setDriverFilter] = useState('all');
    const [fuelTypeFilter, setFuelTypeFilter] = useState('all');

    // Map modal
    const [showMap, setShowMap] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);

    // Inline edit state (management only)
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState(null);

    // Add log form state (management only)
    const [showAddForm, setShowAddForm] = useState(false);
    const [addForm, setAddForm] = useState({ profile_id: '', vehicle_id: '', fuel_type: 'diesel', quantity_litres: '', mileage: '', mileage_unit: 'km' });
    const [addLoading, setAddLoading] = useState(false);
    const [addError, setAddError] = useState(null);

    // Fetch vehicles and all active drivers on mount
    useEffect(() => {
        const loadVehicles = async () => {
            const result = await fetch_all_vehicles();
            if (result.status === 200 && result.data) {
                const active = result.data.filter(v => v.status === 'active' && (v.type === 'UNIT' || v.type === 'RIGID'));
                setVehicles(active);
            }
        };
        const loadAllDrivers = async () => {
            const result = await fetch_all_profiles();
            if (result.status === 200 && result.data) {
                const active = result.data
                    .filter(p => (p.group === 'DRIVER' || p.group === 'ADMIN') && p.status === 'active')
                    .sort((a, b) => a.name.localeCompare(b.name) || a.surname.localeCompare(b.surname));
                setAllDrivers(active);
            }
        };
        loadVehicles();
        loadAllDrivers();
    }, []);

    // Fetch fuel logs on mount with default date range
    useEffect(() => {
        fetchLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Apply filters when logs or filters change
    useEffect(() => {
        let filtered = [...fuelLogs];

        if (vehicleFilter !== 'all') {
            filtered = filtered.filter(log => String(log.vehicle_id) === vehicleFilter);
        }

        if (driverFilter !== 'all') {
            filtered = filtered.filter(log => log.profile_id === driverFilter);
        }

        if (fuelTypeFilter !== 'all') {
            filtered = filtered.filter(log => log.fuel_type?.toLowerCase() === fuelTypeFilter.toLowerCase());
        }

        setFilteredLogs(filtered);
    }, [fuelLogs, vehicleFilter, driverFilter, fuelTypeFilter]);

    const startEdit = (log) => {
        setEditingId(log.id);
        setEditError(null);
        setEditForm({
            fuel_type: log.fuel_type || '',
            quantity_litres: log.quantity_litres ?? '',
            mileage: log.mileage ?? '',
            distance_km: log.distance_km ?? '',
            vehicle_id: String(log.vehicle_id ?? ''),
            profile_id: log.profile_id ?? '',
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditForm({});
        setEditError(null);
    };

    const saveEdit = async (logId) => {
        setEditLoading(true);
        setEditError(null);
        const payload = {
            fuel_type: editForm.fuel_type,
            quantity_litres: editForm.quantity_litres !== '' ? parseFloat(editForm.quantity_litres) : null,
            mileage: editForm.mileage !== '' ? parseInt(editForm.mileage, 10) : null,
            distance_km: editForm.distance_km !== '' ? parseFloat(editForm.distance_km) : null,
            vehicle_id: editForm.vehicle_id !== '' ? parseInt(editForm.vehicle_id, 10) : null,
            profile_id: editForm.profile_id || null,
        };
        const result = await update_fuel_log(logId, payload);
        if (result.status === 200) {
            setFuelLogs(prev => prev.map(l => l.id === logId ? {
                ...l,
                ...payload,
                vehicles: vehicles.find(v => v.id === payload.vehicle_id) || l.vehicles,
                profiles: drivers.find(d => d.id === payload.profile_id)
                    ? { name: drivers.find(d => d.id === payload.profile_id).name, surname: drivers.find(d => d.id === payload.profile_id).surname }
                    : l.profiles,
            } : l));
            setEditingId(null);
            setEditForm({});
        } else {
            setEditError(result.data?.error || 'Failed to update. Please try again.');
        }
        setEditLoading(false);
    };

    const submitAddForm = async () => {
        setAddError(null);
        if (!addForm.profile_id || !addForm.vehicle_id || !addForm.fuel_type || addForm.quantity_litres === '' || addForm.mileage === '') {
            setAddError('Please fill in all required fields.');
            return;
        }
        setAddLoading(true);
        try {
            // Convert mileage to km if entered in miles
            const mileageKm = addForm.mileage_unit === 'miles'
                ? Math.round(parseFloat(addForm.mileage) * 1.60934)
                : parseInt(addForm.mileage, 10);

            // Fetch last log for this vehicle + fuel type to calculate distance
            let distance_km = null;
            const lastResult = await get_last_fuel_log(addForm.vehicle_id, addForm.fuel_type);
            if (lastResult.status === 200 && lastResult.data && lastResult.data.mileage != null) {
                const diff = mileageKm - lastResult.data.mileage;
                if (diff > 0) distance_km = diff;
            }

            const payload = {
                profile_id: addForm.profile_id,
                vehicle_id: parseInt(addForm.vehicle_id, 10),
                fuel_type: addForm.fuel_type,
                quantity_litres: parseFloat(addForm.quantity_litres),
                mileage: mileageKm,
                distance_km,
            };

            const result = await add_fuel_log(payload);
            if (result.status === 201) {
                setShowAddForm(false);
                setAddForm({ profile_id: '', vehicle_id: '', fuel_type: 'diesel', quantity_litres: '', mileage: '', mileage_unit: 'km' });
                // Refresh the log list
                fetchLogs();
            } else {
                setAddError(result.data?.error || 'Failed to add log. Please try again.');
            }
        } catch (err) {
            setAddError('An unexpected error occurred.');
        }
        setAddLoading(false);
    };

    const fetchLogs = async () => {        if (!startDate || !endDate) return;
        setIsLoading(true);
        try {
            const dateRange = {
                startDate: startDate.toISOString().split('T')[0],
                endDate: endDate.toISOString().split('T')[0],
            };
            const result = await fetch_fuel_logs(dateRange);
            if (result.status === 200 && result.data) {
                setFuelLogs(result.data);

                // Extract unique drivers from results
                const uniqueDrivers = [];
                const seen = new Set();
                result.data.forEach(log => {
                    if (log.profiles && !seen.has(log.profile_id)) {
                        seen.add(log.profile_id);
                        uniqueDrivers.push({
                            id: log.profile_id,
                            name: log.profiles.name,
                            surname: log.profiles.surname,
                        });
                    }
                });
                uniqueDrivers.sort((a, b) => a.name.localeCompare(b.name));
                setDrivers(uniqueDrivers);
            } else {
                setFuelLogs([]);
            }
        } catch (err) {
            console.error('Error fetching fuel logs:', err);
            setFuelLogs([]);
        }
        setIsLoading(false);
    };

    const totalLitres = filteredLogs.reduce((sum, log) => sum + (log.quantity_litres || 0), 0);
    const dieselLitres = filteredLogs.filter(l => l.fuel_type?.toLowerCase() === 'diesel').reduce((sum, l) => sum + (l.quantity_litres || 0), 0);
    const adblueLitres = filteredLogs.filter(l => l.fuel_type?.toLowerCase() === 'adblue').reduce((sum, l) => sum + (l.quantity_litres || 0), 0);

    // Calculate L/km from filtered diesel logs that have distance_km
    const dieselLogsWithDistance = filteredLogs.filter(l => l.fuel_type?.toLowerCase() === 'diesel' && l.distance_km > 0);
    const totalDistanceKm = dieselLogsWithDistance.reduce((sum, l) => sum + (l.distance_km || 0), 0);
    const totalDieselForLpKm = dieselLogsWithDistance.reduce((sum, l) => sum + (l.quantity_litres || 0), 0);
    const litresPerKm = totalDistanceKm > 0 ? (totalDieselForLpKm / totalDistanceKm) : 0;

    const cardStyle = {
        background: theme === 'dark' ? '#1e1e2f' : '#fff',
        color: theme === 'dark' ? '#e0e0e0' : '#333',
        border: theme === 'dark' ? '1px solid #333' : '1px solid #ddd',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px',
    };

    const headerStyle = {
        background: theme === 'dark' ? '#2a2a3d' : '#f5f7fa',
        padding: '10px 14px',
        fontWeight: 'bold',
        borderBottom: theme === 'dark' ? '1px solid #444' : '1px solid #ddd',
    };

    const rowStyle = (index) => ({
        padding: '10px 14px',
        borderBottom: theme === 'dark' ? '1px solid #333' : '1px solid #eee',
        background: index % 2 === 0
            ? (theme === 'dark' ? '#1e1e2f' : '#fff')
            : (theme === 'dark' ? '#252540' : '#f9f9f9'),
    });

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, color: theme === 'dark' ? '#e0e0e0' : '#333' }}>
                    Fuel Logs
                </h2>
                {isManagement && (
                    <button
                        onClick={() => { setShowAddForm(true); setAddError(null); }}
                        style={{
                            padding: '8px 18px',
                            background: '#27ae60',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '14px',
                        }}
                    >
                        + Add Log
                    </button>
                )}
            </div>

            {/* Add Log Modal */}
            {isManagement && showAddForm && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
                    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{
                        background: theme === 'dark' ? '#1e1e2f' : '#fff',
                        color: theme === 'dark' ? '#e0e0e0' : '#333',
                        borderRadius: '10px',
                        padding: '28px 32px',
                        width: '100%',
                        maxWidth: '480px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
                    }}>
                        <h3 style={{ margin: '0 0 20px' }}>Add Fuel Log</h3>

                        <div style={{ marginBottom: '14px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>Driver *</label>
                            <select
                                value={addForm.profile_id}
                                onChange={e => setAddForm(f => ({ ...f, profile_id: e.target.value }))}
                                className="form-control"
                            >
                                <option value="">— Select Driver —</option>
                                {allDrivers.map(d => (
                                    <option key={d.id} value={d.id}>{d.name} {d.surname}</option>
                                ))}
                            </select>
                        </div>

                        <div style={{ marginBottom: '14px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>Vehicle *</label>
                            <select
                                value={addForm.vehicle_id}
                                onChange={e => setAddForm(f => ({ ...f, vehicle_id: e.target.value }))}
                                className="form-control"
                            >
                                <option value="">— Select Vehicle —</option>
                                <option disabled>───── Rigids ─────</option>
                                {vehicles.filter(v => v.type === 'RIGID').sort((a, b) => a.reg.localeCompare(b.reg)).map(v => (
                                    <option key={v.id} value={String(v.id)}>{v.reg}</option>
                                ))}
                                <option disabled>───── Units ─────</option>
                                {vehicles.filter(v => v.type === 'UNIT').sort((a, b) => a.reg.localeCompare(b.reg)).map(v => (
                                    <option key={v.id} value={String(v.id)}>{v.reg}</option>
                                ))}
                            </select>
                        </div>

                        <div style={{ marginBottom: '14px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>Fuel Type *</label>
                            <select
                                value={addForm.fuel_type}
                                onChange={e => setAddForm(f => ({ ...f, fuel_type: e.target.value }))}
                                className="form-control"
                            >
                                <option value="diesel">Diesel</option>
                                <option value="adblue">AdBlue</option>
                            </select>
                        </div>

                        <div style={{ marginBottom: '14px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>Quantity (Litres) *</label>
                            <input
                                type="number"
                                min="0"
                                step="0.1"
                                value={addForm.quantity_litres}
                                onChange={e => setAddForm(f => ({ ...f, quantity_litres: e.target.value }))}
                                className="form-control"
                                placeholder="e.g. 120.5"
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>Mileage *</label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={addForm.mileage}
                                    onChange={e => setAddForm(f => ({ ...f, mileage: e.target.value }))}
                                    className="form-control"
                                    placeholder={addForm.mileage_unit === 'miles' ? 'e.g. 85000' : 'e.g. 136000'}
                                    style={{ flex: 1 }}
                                />
                                <select
                                    value={addForm.mileage_unit}
                                    onChange={e => setAddForm(f => ({ ...f, mileage_unit: e.target.value }))}
                                    className="form-control"
                                    style={{ width: '90px' }}
                                >
                                    <option value="km">km</option>
                                    <option value="miles">miles</option>
                                </select>
                            </div>
                            {addForm.mileage_unit === 'miles' && addForm.mileage !== '' && (
                                <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                                    ≈ {Math.round(parseFloat(addForm.mileage) * 1.60934).toLocaleString()} km will be saved
                                </div>
                            )}
                        </div>

                        {addError && (
                            <div style={{ color: '#e74c3c', fontSize: '13px', marginBottom: '14px' }}>{addError}</div>
                        )}

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => { setShowAddForm(false); setAddForm({ profile_id: '', vehicle_id: '', fuel_type: 'diesel', quantity_litres: '', mileage: '', mileage_unit: 'km' }); setAddError(null); }}
                                disabled={addLoading}
                                style={{
                                    padding: '8px 18px',
                                    background: 'transparent',
                                    color: theme === 'dark' ? '#aaa' : '#555',
                                    border: theme === 'dark' ? '1px solid #555' : '1px solid #ccc',
                                    borderRadius: '6px',
                                    cursor: addLoading ? 'not-allowed' : 'pointer',
                                    fontWeight: '600',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitAddForm}
                                disabled={addLoading}
                                style={{
                                    padding: '8px 18px',
                                    background: '#27ae60',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: addLoading ? 'not-allowed' : 'pointer',
                                    fontWeight: '600',
                                }}
                            >
                                {addLoading ? 'Saving...' : 'Save Log'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Date Range + Filters */}
            <div style={{ ...cardStyle, display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
                <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>Start Date</label>
                    <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        dateFormat="dd/MM/yyyy"
                        className="form-control"
                        maxDate={endDate}
                    />
                </div>
                <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>End Date</label>
                    <DatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        dateFormat="dd/MM/yyyy"
                        className="form-control"
                        minDate={startDate}
                        maxDate={new Date()}
                    />
                </div>
                <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>Truck</label>
                    <select
                        value={vehicleFilter}
                        onChange={(e) => setVehicleFilter(e.target.value)}
                        className="form-control"
                        style={{ minWidth: '160px' }}
                    >
                        <option value="all">All Trucks</option>
                        <option disabled>───── Rigids ─────</option>
                        {vehicles.filter(v => v.type === 'RIGID').sort((a, b) => a.reg.localeCompare(b.reg)).map(v => (
                            <option key={v.id} value={String(v.id)}>{v.reg}</option>
                        ))}
                        <option disabled>───── Units ─────</option>
                        {vehicles.filter(v => v.type === 'UNIT').sort((a, b) => a.reg.localeCompare(b.reg)).map(v => (
                            <option key={v.id} value={String(v.id)}>{v.reg}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>Driver</label>
                    <select
                        value={driverFilter}
                        onChange={(e) => setDriverFilter(e.target.value)}
                        className="form-control"
                        style={{ minWidth: '160px' }}
                    >
                        <option value="all">All Drivers</option>
                        {drivers.sort((a, b) => a.name.localeCompare(b.name) || a.surname.localeCompare(b.surname)).map(d => (
                            <option key={d.id} value={d.id}>{d.name} {d.surname}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>Fuel Type</label>
                    <select
                        value={fuelTypeFilter}
                        onChange={(e) => setFuelTypeFilter(e.target.value)}
                        className="form-control"
                        style={{ minWidth: '140px' }}
                    >
                        <option value="all">All Types</option>
                        <option value="diesel">Diesel</option>
                        <option value="adblue">AdBlue</option>
                    </select>
                </div>
                <div>
                    <button
                        onClick={fetchLogs}
                        disabled={isLoading}
                        style={{
                            padding: '8px 20px',
                            background: '#1997eb',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                            fontWeight: '600',
                        }}
                    >
                        {isLoading ? 'Loading...' : 'Search'}
                    </button>
                </div>
            </div>

            {/* Summary Cards */}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
                <div style={{ ...cardStyle, flex: 1, minWidth: '150px', textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', opacity: 0.7 }}>Total Entries</div>
                    <div style={{ fontSize: '28px', fontWeight: '700' }}>{filteredLogs.length}</div>
                </div>
                <div style={{ ...cardStyle, flex: 1, minWidth: '150px', textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', opacity: 0.7 }}>Total Litres</div>
                    <div style={{ fontSize: '28px', fontWeight: '700' }}>{totalLitres.toFixed(1)}</div>
                </div>
                <div style={{ ...cardStyle, flex: 1, minWidth: '150px', textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', opacity: 0.7 }}>Diesel (L)</div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#e67e22' }}>{dieselLitres.toFixed(1)}</div>
                </div>
                <div style={{ ...cardStyle, flex: 1, minWidth: '150px', textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', opacity: 0.7 }}>AdBlue (L)</div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#3498db' }}>{adblueLitres.toFixed(1)}</div>
                </div>
                <div style={{ ...cardStyle, flex: 1, minWidth: '150px', textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', opacity: 0.7 }}>L/km</div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: litresPerKm > 0 && litresPerKm <= 0.40 ? '#27ae60' : litresPerKm > 0 ? '#e67e22' : '#999' }}>
                        {litresPerKm > 0 ? litresPerKm.toFixed(2) : '—'}
                    </div>
                </div>
            </div>

            {/* Table */}
            {isLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <ActivityIndicator />
                </div>
            ) : filteredLogs.length === 0 ? (
                <div style={{ ...cardStyle, textAlign: 'center', padding: '40px', opacity: 0.6 }}>
                    No fuel logs found for the selected filters.
                </div>
            ) : (
                <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={headerStyle}>
                                    <th style={{ padding: '10px 14px', textAlign: 'left' }}>Date</th>
                                    <th style={{ padding: '10px 14px', textAlign: 'left' }}>Time</th>
                                    <th style={{ padding: '10px 14px', textAlign: 'left' }}>Driver</th>
                                    <th style={{ padding: '10px 14px', textAlign: 'left' }}>Vehicle</th>
                                    <th style={{ padding: '10px 14px', textAlign: 'left' }}>Fuel Type</th>
                                    <th style={{ padding: '10px 14px', textAlign: 'right' }}>Litres</th>
                                    <th style={{ padding: '10px 14px', textAlign: 'right' }}>Mileage</th>
                                    <th style={{ padding: '10px 14px', textAlign: 'right' }}>Distance (km)</th>
                                    <th style={{ padding: '10px 14px', textAlign: 'center' }}>Location</th>
                                    {isManagement && (
                                        <th style={{ padding: '10px 14px', textAlign: 'center' }}>Actions</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredLogs.map((log, index) => {
                                    const isEditing = isManagement && editingId === log.id;
                                    return (
                                    <tr key={log.id} style={rowStyle(index)}>
                                        <td style={{ padding: '10px 14px' }}>
                                            {new Date(log.created_at).toLocaleDateString('en-GB')}
                                        </td>
                                        <td style={{ padding: '10px 14px' }}>
                                            {new Date(log.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })}
                                        </td>
                                        <td style={{ padding: '10px 14px' }}>
                                            {isEditing ? (
                                                <select
                                                    value={editForm.profile_id}
                                                    onChange={e => setEditForm(f => ({ ...f, profile_id: e.target.value }))}
                                                    className="form-control form-control-sm"
                                                    style={{ minWidth: '130px' }}
                                                >
                                                    {allDrivers.map(d => (
                                                        <option key={d.id} value={d.id}>{d.name} {d.surname}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                log.profiles ? `${log.profiles.name} ${log.profiles.surname}` : '-'
                                            )}
                                        </td>
                                        <td style={{ padding: '10px 14px' }}>
                                            {isEditing ? (
                                                <select
                                                    value={editForm.vehicle_id}
                                                    onChange={e => setEditForm(f => ({ ...f, vehicle_id: e.target.value }))}
                                                    className="form-control form-control-sm"
                                                    style={{ minWidth: '100px' }}
                                                >
                                                    {vehicles.map(v => (
                                                        <option key={v.id} value={String(v.id)}>{v.reg}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                log.vehicles?.reg || '-'
                                            )}
                                        </td>
                                        <td style={{ padding: '10px 14px' }}>
                                            {isEditing ? (
                                                <select
                                                    value={editForm.fuel_type}
                                                    onChange={e => setEditForm(f => ({ ...f, fuel_type: e.target.value }))}
                                                    className="form-control form-control-sm"
                                                    style={{ minWidth: '100px' }}
                                                >
                                                    <option value="diesel">Diesel</option>
                                                    <option value="adblue">AdBlue</option>
                                                </select>
                                            ) : (
                                                <span style={{
                                                    padding: '2px 10px',
                                                    borderRadius: '12px',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    background: log.fuel_type?.toLowerCase() === 'diesel' ? '#fdebd0' : '#d6eaf8',
                                                    color: log.fuel_type?.toLowerCase() === 'diesel' ? '#b7530d' : '#1a5276',
                                                }}>
                                                    {log.fuel_type}
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                                            {isEditing ? (
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.1"
                                                    value={editForm.quantity_litres}
                                                    onChange={e => setEditForm(f => ({ ...f, quantity_litres: e.target.value }))}
                                                    className="form-control form-control-sm"
                                                    style={{ width: '80px', textAlign: 'right' }}
                                                />
                                            ) : (
                                                log.quantity_litres?.toFixed(1)
                                            )}
                                        </td>
                                        <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                                            {isEditing ? (
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="1"
                                                    value={editForm.mileage}
                                                    onChange={e => setEditForm(f => ({ ...f, mileage: e.target.value }))}
                                                    className="form-control form-control-sm"
                                                    style={{ width: '90px', textAlign: 'right' }}
                                                />
                                            ) : (
                                                log.mileage?.toLocaleString()
                                            )}
                                        </td>
                                        <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                                            {isEditing ? (
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.1"
                                                    value={editForm.distance_km}
                                                    onChange={e => setEditForm(f => ({ ...f, distance_km: e.target.value }))}
                                                    className="form-control form-control-sm"
                                                    style={{ width: '80px', textAlign: 'right' }}
                                                />
                                            ) : (
                                                log.distance_km != null ? log.distance_km.toFixed(1) : '—'
                                            )}
                                        </td>
                                        <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                            {log.latitude && log.longitude ? (
                                                <button
                                                    onClick={() => {
                                                        setSelectedLocation({ lat: log.latitude, lng: log.longitude });
                                                        setShowMap(true);
                                                    }}
                                                    style={{
                                                        padding: '4px 10px',
                                                        background: 'transparent',
                                                        color: '#1997eb',
                                                        border: '1px solid #1997eb',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                    }}
                                                >
                                                    📍 Map
                                                </button>
                                            ) : (
                                                <span style={{ color: '#999', fontSize: '12px' }}>—</span>
                                            )}
                                        </td>
                                        {isManagement && (
                                            <td style={{ padding: '10px 14px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                {isEditing ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                                                        <div style={{ display: 'flex', gap: '6px' }}>
                                                            <button
                                                                onClick={() => saveEdit(log.id)}
                                                                disabled={editLoading}
                                                                style={{
                                                                    padding: '4px 12px',
                                                                    background: '#27ae60',
                                                                    color: '#fff',
                                                                    border: 'none',
                                                                    borderRadius: '4px',
                                                                    cursor: editLoading ? 'not-allowed' : 'pointer',
                                                                    fontSize: '12px',
                                                                    fontWeight: '600',
                                                                }}
                                                            >
                                                                {editLoading ? '...' : 'Save'}
                                                            </button>
                                                            <button
                                                                onClick={cancelEdit}
                                                                disabled={editLoading}
                                                                style={{
                                                                    padding: '4px 12px',
                                                                    background: 'transparent',
                                                                    color: '#e74c3c',
                                                                    border: '1px solid #e74c3c',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '12px',
                                                                    fontWeight: '600',
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                        {editError && (
                                                            <span style={{ color: '#e74c3c', fontSize: '11px', maxWidth: '180px', textAlign: 'center' }}>
                                                                {editError}
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => startEdit(log)}
                                                        style={{
                                                            padding: '4px 12px',
                                                            background: 'transparent',
                                                            color: '#1997eb',
                                                            border: '1px solid #1997eb',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '12px',
                                                            fontWeight: '600',
                                                        }}
                                                    >
                                                        ✏️ Edit
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <LocationMapModal
                show={showMap}
                onHide={() => setShowMap(false)}
                location={selectedLocation}
                title="Fuel Log Location"
            />
        </div>
    );
};

export default FuelLogs;
